import { useMutation } from "@apollo/client";
import AddAlert from "@mui/icons-material/AddAlert";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import {
  getNotificationCategoryLabel,
  getNotificationProgramAreaLabel,
} from "components/Notifications/notificationPresentation";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  NotificationCategory,
  NotificationPriority,
  NotificationsQuery,
  NotificationsQueryVariables,
  ProgramArea,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { GET_NOTIFICATIONS, QUEUE_STAFF_NOTIFICATION } from "hooks/useNotifications";
import { useState } from "react";

type NotificationRow = NotificationsQuery["notifications"]["items"][number];

const notificationCategories = Object.values(NotificationCategory);
const notificationPriorities = Object.values(NotificationPriority);
const notificationProgramAreas = Object.values(ProgramArea);

function isNotificationCategory(value: string): value is NotificationCategory {
  return notificationCategories.some((category) => category === value);
}

function isNotificationPriority(value: string): value is NotificationPriority {
  return notificationPriorities.some((priority) => priority === value);
}

function isProgramArea(value: string): value is ProgramArea {
  return notificationProgramAreas.some((programArea) => programArea === value);
}

function parseUserIds(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((userId) => userId.trim())
    .filter((userId) => userId.length > 0);
}

export const Notifications = () => {
  useBreadcrumb({
    label: "Notifications",
  });
  const [queueDialogOpen, setQueueDialogOpen] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [userIdsText, setUserIdsText] = useState("");
  const [title, setTitle] = useState("");
  const [bodyMarkdown, setBodyMarkdown] = useState("");
  const [category, setCategory] = useState<NotificationCategory>(NotificationCategory.Staff);
  const [programArea, setProgramArea] = useState<ProgramArea | "">("");
  const [priority, setPriority] = useState<NotificationPriority>(NotificationPriority.Normal);
  const [queueStaffNotification, queueState] = useMutation(QUEUE_STAFF_NOTIFICATION, {
    refetchQueries: [GET_NOTIFICATIONS],
  });

  const closeQueueDialog = () => {
    setQueueDialogOpen(false);
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    const nextCategory = event.target.value;
    if (isNotificationCategory(nextCategory)) {
      setCategory(nextCategory);
    }
  };

  const handlePriorityChange = (event: SelectChangeEvent<string>) => {
    const nextPriority = event.target.value;
    if (isNotificationPriority(nextPriority)) {
      setPriority(nextPriority);
    }
  };

  const handleProgramAreaChange = (event: SelectChangeEvent<string>) => {
    const nextProgramArea = event.target.value;
    if (nextProgramArea === "" || isProgramArea(nextProgramArea)) {
      setProgramArea(nextProgramArea);
    }
  };

  const handleQueueNotification = async () => {
    await queueStaffNotification({
      variables: {
        input: {
          userIds: parseUserIds(userIdsText),
          tenantId: tenantId.trim() || null,
          title,
          bodyMarkdown,
          category,
          programArea: programArea || null,
          priority,
        },
      },
    });
    setTenantId("");
    setUserIdsText("");
    setTitle("");
    setBodyMarkdown("");
    setProgramArea("");
    closeQueueDialog();
  };

  const canQueue =
    parseUserIds(userIdsText).length > 0 &&
    title.trim().length > 0 &&
    bodyMarkdown.trim().length > 0;

  return (
    <Stack py={3}>
      <OmnisearchDataGrid<NotificationsQuery, NotificationsQueryVariables, NotificationRow>
        persistenceKey="staffNotifications"
        isURLDriven
        commandButtons={[
          <Button
            key="queue-notification"
            startIcon={<AddAlert />}
            variant="contained"
            onClick={() => setQueueDialogOpen(true)}
          >
            Queue notification
          </Button>,
        ]}
        columns={[
          {
            field: "tenant",
            headerName: "Organization",
            flex: 0.5,
            filterKeyType: "multipleTenant",
            valueGetter(_, row) {
              return row.tenant?.name;
            },
          },
          {
            field: "title",
            headerName: "Title",
            flex: 0.7,
          },
          {
            field: "message",
            headerName: "Message",
            flex: 1,
          },
          {
            field: "facility",
            headerName: "Facility",
            flex: 0.5,
            valueGetter(_, row) {
              return row.facility?.name;
            },
          },
          {
            field: "programArea",
            headerName: "Program",
            flex: 0.35,
            valueGetter(_, row) {
              return row.programArea ? getNotificationProgramAreaLabel(row.programArea) : "";
            },
          },
          {
            field: "tierIIReport",
            headerName: "Tier II Report",
            flex: 0.5,
            sortable: false,
            valueGetter(_, row) {
              const report = row.tierIIReport;

              if (!report) {
                return "";
              }

              return `${report.reportKind} - ${report.reportingYear}`;
            },
          },
        ]}
        dataQuery={GET_NOTIFICATIONS}
        getItems={(data) => data.notifications.items}
        getCount={(data) => data.notifications.count}
        excludeFilterColumns={["tierIIReport"]}
      />
      {queueDialogOpen ? (
        <Dialog
          open
          onClose={closeQueueDialog}
          title="Queue notification"
          maxWidth="sm"
          fullWidth
          primaryActionButtonProps={{
            saveText: "Queue",
            loading: queueState.loading,
            disabled: !canQueue,
            onClick: handleQueueNotification,
          }}
        >
          <TextField
            label="Tenant ID"
            value={tenantId}
            onChange={(event) => setTenantId(event.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="User IDs"
            value={userIdsText}
            onChange={(event) => setUserIdsText(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Body markdown"
            value={bodyMarkdown}
            onChange={(event) => setBodyMarkdown(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={5}
          />
          <Stack direction="row" spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="notification-category-label">Category</InputLabel>
              <Select
                labelId="notification-category-label"
                label="Category"
                value={category}
                onChange={handleCategoryChange}
              >
                {notificationCategories.map((value) => (
                  <MenuItem key={value} value={value}>
                    {getNotificationCategoryLabel(value)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="notification-program-area-label">Program</InputLabel>
              <Select
                labelId="notification-program-area-label"
                label="Program"
                value={programArea}
                onChange={handleProgramAreaChange}
              >
                <MenuItem value="">None</MenuItem>
                {notificationProgramAreas.map((value) => (
                  <MenuItem key={value} value={value}>
                    {getNotificationProgramAreaLabel(value)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="notification-priority-label">Priority</InputLabel>
              <Select
                labelId="notification-priority-label"
                label="Priority"
                value={priority}
                onChange={handlePriorityChange}
              >
                {notificationPriorities.map((value) => (
                  <MenuItem key={value} value={value}>
                    {prettyPrintEnumValue(value)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Dialog>
      ) : null}
    </Stack>
  );
};
